/**
 * events/reducer.ts — `applyEvent(state, event) → state'` (tech/21 §4, D14).
 *
 * Reducer — ЕДИНСТВЕННОЕ место, где GameState меняется. Он не вызывает хуки
 * и НЕ пишет журнал в состояние (D27/R14): журнал выводится из потока событий
 * и живёт снаружи.
 *
 * ФОРМА МУТАЦИИ (выбор зафиксирован в M2): контролируемая мутация ЧЕРНОВИКА.
 * applyEvent мутирует ПЕРЕДАННЫЙ объект in-place и возвращает его же. Границу
 * чистоты держит драйвер (runStep/runOp): вход копируется целиком ОДИН раз
 * (structuredClone) на входе шага, дальше все события шага мутируют черновик.
 * Почему так, а не immutable-спреды:
 *  - императивные ops (D14) читают/пишут один живой черновик — «следующая
 *    строка видит эффект предыдущей» выполняется тривиально;
 *  - in-place мутация НЕ пересоздаёт объекты и потому не меняет порядок
 *    вставки ключей Record — байт-стабильность serialize (tech/25 §1,
 *    находка ревью M1) сохраняется бесплатно;
 *  - снаружи мутация не наблюдаема: вход runStep/runOp не трогается.
 *
 * Арифметика урона/статов нормализует -0 → 0 (normalizeZero, находка ревью M1):
 * JSON превращает -0 в 0, и без нормализации round-trip/golden-хэш разошлись бы.
 */
import { requireCell, requireInstance, requirePlayer } from '../state/access.js';
import { makeCardInstance } from '../state/instance.js';
import { normalizeZero } from '../state/num.js';
import { effectiveStats } from '../stats/effectiveStats.js';
/** Компайл-тайм гарантия обработки всех видов GameEvent. */
function assertNever(x) {
    throw new Error(`applyEvent: необработанный вид события ${JSON.stringify(x)}`);
}
/** Массив зоны игрока-владельца; board/attachment живут в отдельных структурах. */
function zoneArray(state, ownerId, zone, where) {
    const player = requirePlayer(state, ownerId, where);
    return zone === 'deck' ? player.deck
        : zone === 'hand' ? player.hand
            : zone === 'discard' ? player.discard
                : player.armedReactions;
}
/** Не оставлять сериализуемые ссылки lastDamageSourceId на ушедший источник. */
function clearLastDamageSourceReferences(state, sourceId) {
    for (const instance of Object.values(state.instances)) {
        if (instance.vars.lastDamageSourceId === sourceId)
            delete instance.vars.lastDamageSourceId;
    }
}
/**
 * Применить событие к черновику состояния. Мутирует ПЕРЕДАННЫЙ объект и
 * возвращает его же (см. шапку модуля про границу чистоты).
 */
export function applyEvent(state, event) {
    switch (event.type) {
        case 'CardPlanned': {
            const player = requirePlayer(state, event.playerId, 'applyEvent(CardPlanned)');
            if ((event.plan.kind === undefined || event.plan.kind === 'FIELD') && event.plan.selections !== undefined) {
                const placement = event.plan.selections.find((step) => step.stepId === 'placement')?.selection;
                const candidate = placement?.[0];
                if (placement?.length !== 1 || candidate?.kind !== 'cell' || candidate.cellId !== event.plan.cellId) {
                    throw new Error('applyEvent(CardPlanned): FIELD cellId должен быть derived из единственного placement selection');
                }
            }
            player.staged.push(structuredClone(event.plan));
            if ('planSeq' in event.plan)
                player.nextPlanSeq = Math.max(player.nextPlanSeq, event.plan.planSeq + 1);
            return state;
        }
        case 'CardUnplanned': {
            const player = requirePlayer(state, event.playerId, 'applyEvent(CardUnplanned)');
            const index = player.staged.findIndex((plan) => plan.instanceId === event.instanceId);
            if (index >= 0)
                player.staged.splice(index, 1);
            return state;
        }
        case 'PlacementStaged': {
            const player = requirePlayer(state, event.playerId, 'applyEvent(PlacementStaged)');
            player.staged.push(structuredClone(event.placement));
            return state;
        }
        case 'PlacementUnstaged': {
            const player = requirePlayer(state, event.playerId, 'applyEvent(PlacementUnstaged)');
            const index = player.staged.findIndex((p) => 'cellId' in p && p.cellId === event.cellId);
            if (index >= 0)
                player.staged.splice(index, 1);
            return state;
        }
        case 'PlayerReadyChanged': {
            requirePlayer(state, event.playerId, 'applyEvent(PlayerReadyChanged)').ready = event.ready;
            return state;
        }
        case 'ResourcesChanged': {
            const player = requirePlayer(state, event.playerId, 'applyEvent(ResourcesChanged)');
            player.resources = {
                energy: event.energy,
                energyMax: event.energyMax,
                temporaryEnergy: player.resources.temporaryEnergy,
                temporaryEnergyRound: player.resources.temporaryEnergyRound,
                ...(player.resources.queuedTemporaryEnergy !== undefined
                    ? { queuedTemporaryEnergy: player.resources.queuedTemporaryEnergy }
                    : {}),
                ...(player.resources.queuedTemporaryEnergyRound !== undefined
                    ? { queuedTemporaryEnergyRound: player.resources.queuedTemporaryEnergyRound }
                    : {}),
            };
            return state;
        }
        case 'EffectDurationChanged': {
            const effect = state.effects.find((e) => e.id === event.effectId);
            if (effect === undefined) {
                throw new Error(`applyEvent(EffectDurationChanged): неизвестный эффект "${event.effectId}"`);
            }
            effect.duration = event.duration;
            return state;
        }
        // --- рамочные факты: данные раунда/фазы ---
        case 'RoundStarted': {
            state.round = event.round;
            for (const player of Object.values(state.players)) {
                if (player.resources.queuedTemporaryEnergyRound !== event.round)
                    continue;
                const queued = player.resources.queuedTemporaryEnergy ?? 0;
                if (player.resources.temporaryEnergy > 0 &&
                    player.resources.temporaryEnergyRound !== event.round) {
                    throw new Error('applyEvent(RoundStarted): предыдущая временная энергия не истекла');
                }
                player.resources.temporaryEnergy =
                    (player.resources.temporaryEnergyRound === event.round
                        ? player.resources.temporaryEnergy
                        : 0) + queued;
                player.resources.temporaryEnergyRound = event.round;
                delete player.resources.queuedTemporaryEnergy;
                delete player.resources.queuedTemporaryEnergyRound;
            }
            return state;
        }
        case 'PhaseChanged': {
            state.phase = event.phase;
            return state;
        }
        // Факты без собственного изменения данных в M2 (оркестрация раунда/боя — M4):
        case 'RoundEnded':
        case 'CombatStarted':
        case 'CombatEnded':
        case 'FieldRevealCompleted':
        case 'UnitAppeared':
        case 'HackTriggered':
        case 'ReactionTriggered':
        case 'AttachmentTriggered':
        case 'CaptureFizzled':
        case 'AttackAvoidanceChecked':
        case 'AttackResolved':
            return state;
        // Добор: зонный перенос делает ПАРНЫЙ CardMoved (tech/21 §7) — здесь no-op,
        // иначе клиентский reducer-only реплей (D22) применил бы перенос дважды.
        case 'CardDrawn':
            return state;
        case 'CardRevealed':
        case 'CardResolved':
        case 'CardFizzled':
        case 'ReactionArmed':
            return state;
        case 'UnitPlaced': {
            // Материальное размещение без запуска Появления FIELD-карты.
            const where = 'applyEvent(UnitPlaced)';
            const inst = requireInstance(state, event.instanceId, where);
            const cell = requireCell(state, event.cellId, where);
            if (inst.zone !== 'board') {
                throw new Error(`${where}: экземпляр "${inst.id}" должен быть уже перемещён в board`);
            }
            inst.enteredRound = state.round;
            inst.controllerId = inst.ownerId;
            if (cell.occupantId === inst.id && inst.cellId === cell.id)
                return state;
            if (cell.occupantId !== null) {
                throw new Error(`${where}: ячейка "${cell.id}" занята ("${cell.occupantId}")`);
            }
            cell.occupantId = inst.id;
            inst.cellId = cell.id;
            return state;
        }
        case 'UnitEnteredPlay': {
            const where = 'applyEvent(UnitEnteredPlay)';
            const inst = requireInstance(state, event.instanceId, where);
            const cell = requireCell(state, event.cellId, where);
            if (inst.zone !== 'board') {
                throw new Error(`${where}: экземпляр "${inst.id}" должен быть уже перемещён в board`);
            }
            // Появление идемпотентно по занятию ячейки: токен размещается атомарно уже в
            // TokenCreated, а planned FIELD-карта — предшествующим UnitPlaced. Событие
            // остаётся авторитетным фактом входа для onEnterPlay/onAppear.
            inst.enteredRound = state.round;
            inst.controllerId = inst.ownerId;
            if (cell.occupantId === inst.id && inst.cellId === cell.id)
                return state;
            if (cell.occupantId !== null) {
                throw new Error(`${where}: ячейка "${cell.id}" занята ("${cell.occupantId}")`);
            }
            cell.occupantId = inst.id;
            inst.cellId = cell.id;
            return state;
        }
        case 'HeatChanged': {
            const inst = requireInstance(state, event.instanceId, 'applyEvent(HeatChanged)');
            inst.heat = event.current;
            return state;
        }
        case 'FreezeChanged': {
            const inst = requireInstance(state, event.instanceId, 'applyEvent(FreezeChanged)');
            inst.freeze = event.current;
            return state;
        }
        case 'ShieldAbsorbed': {
            const effect = state.effects.find((candidate) => candidate.id === event.effectId);
            if (effect?.shield === undefined) {
                throw new Error(`applyEvent(ShieldAbsorbed): неизвестный Щит "${event.effectId}"`);
            }
            effect.shield.remaining = event.remaining;
            return state;
        }
        case 'ShieldDepleted':
        case 'AttackPerformed':
            return state;
        case 'HackApplied': {
            const marker = event.marker;
            const victim = requireInstance(state, marker.victimInstanceId, 'applyEvent(HackApplied)');
            if (victim.hack !== undefined)
                throw new Error('applyEvent(HackApplied): прежний Взлом должен быть снят явно');
            victim.hack = structuredClone(marker);
            state.nextHackSeq = Math.max(state.nextHackSeq, marker.seq + 1);
            return state;
        }
        case 'HackRemoved': {
            const victim = requireInstance(state, event.victimInstanceId, 'applyEvent(HackRemoved)');
            if (victim.hack?.hackId !== event.hackId) {
                throw new Error(`applyEvent(HackRemoved): Взлом "${event.hackId}" не найден на жертве`);
            }
            delete victim.hack;
            return state;
        }
        case 'UnitMoved': {
            const where = 'applyEvent(UnitMoved)';
            const inst = requireInstance(state, event.instanceId, where);
            const from = requireCell(state, event.from, where);
            const to = requireCell(state, event.to, where);
            if (from.occupantId !== inst.id) {
                throw new Error(`${where}: в ячейке "${event.from}" стоит не "${inst.id}" (occupantId: ${String(from.occupantId)})`);
            }
            if (to.occupantId !== null) {
                throw new Error(`${where}: ячейка "${event.to}" занята ("${to.occupantId}")`);
            }
            from.occupantId = null;
            to.occupantId = inst.id;
            inst.cellId = event.to;
            return state;
        }
        case 'AttachmentAdded': {
            if (state.attachments.some((candidate) => candidate.attachmentId === event.attachment.attachmentId)) {
                throw new Error(`applyEvent(AttachmentAdded): attachment уже существует`);
            }
            state.attachments.push(structuredClone(event.attachment));
            state.nextAttachmentSeq = Math.max(state.nextAttachmentSeq, event.attachment.seq + 1);
            return state;
        }
        case 'AttachmentRemoved': {
            const index = state.attachments.findIndex((candidate) => candidate.attachmentId === event.attachmentId);
            if (index < 0)
                throw new Error(`applyEvent(AttachmentRemoved): attachment не найден`);
            state.attachments.splice(index, 1);
            return state;
        }
        case 'CaptureApplied': {
            const inst = requireInstance(state, event.instanceId, 'applyEvent(CaptureApplied)');
            const from = requireCell(state, event.fromCellId, 'applyEvent(CaptureApplied)');
            const to = requireCell(state, event.toCellId, 'applyEvent(CaptureApplied)');
            if (from.occupantId !== inst.id || to.occupantId !== null) {
                throw new Error('applyEvent(CaptureApplied): неконсистентные source/destination');
            }
            from.occupantId = null;
            to.occupantId = inst.id;
            inst.cellId = to.id;
            inst.controllerId = event.newControllerId;
            if (event.ignoreSummoningDelay === true)
                inst.enteredRound = Math.max(0, state.round - 1);
            if (event.reservation !== undefined)
                state.captureReservations.push(structuredClone(event.reservation));
            state.nextCaptureSeq = Math.max(state.nextCaptureSeq, event.seq + 1);
            return state;
        }
        case 'CaptureReturned': {
            const reservationIndex = state.captureReservations.findIndex((candidate) => candidate.captureId === event.captureId);
            if (reservationIndex >= 0)
                state.captureReservations.splice(reservationIndex, 1);
            const inst = state.instances[event.instanceId];
            if (event.reason === 'expired' && inst !== undefined && inst.zone === 'board' && inst.cellId !== undefined) {
                const from = requireCell(state, inst.cellId, 'applyEvent(CaptureReturned)');
                const to = requireCell(state, event.toCellId, 'applyEvent(CaptureReturned)');
                if (from.occupantId === inst.id)
                    from.occupantId = null;
                if (to.occupantId !== null)
                    throw new Error('applyEvent(CaptureReturned): source cell занята');
                to.occupantId = inst.id;
                inst.cellId = to.id;
                inst.controllerId = event.restoredControllerId;
            }
            return state;
        }
        case 'ExtraAttackQueued':
            state.pendingExtraAttacks.push(structuredClone(event.request));
            state.nextExtraAttackSeq = Math.max(state.nextExtraAttackSeq, event.request.seq + 1);
            return state;
        case 'ExtraAttackResolved': {
            const index = state.pendingExtraAttacks.findIndex((request) => request.id === event.requestId);
            if (index >= 0)
                state.pendingExtraAttacks.splice(index, 1);
            return state;
        }
        case 'CardMoved': {
            const where = 'applyEvent(CardMoved)';
            const inst = requireInstance(state, event.instanceId, where);
            if (inst.zone !== event.from) {
                throw new Error(`${where}: карта "${inst.id}" в зоне '${inst.zone}', а событие ожидает '${event.from}'`);
            }
            if (event.from === event.to) {
                throw new Error(`${where}: зоны from и to совпадают ('${event.from}')`);
            }
            if (event.from === 'board') {
                clearLastDamageSourceReferences(state, inst.id);
                // Снятие с поля: ячейка могла быть уже освобождена парным UnitDied (destroy).
                if (inst.cellId !== undefined) {
                    const cell = state.board.cells[inst.cellId];
                    if (cell !== undefined && cell.occupantId === inst.id) {
                        cell.occupantId = null;
                    }
                    delete inst.cellId;
                }
            }
            else if (event.from !== 'attachment') {
                const src = zoneArray(state, inst.ownerId, event.from, where);
                const i = src.indexOf(inst.id);
                if (i < 0) {
                    throw new Error(`${where}: карта "${inst.id}" не найдена в зоне '${event.from}' игрока "${inst.ownerId}"`);
                }
                src.splice(i, 1);
            }
            if (event.to !== 'board' && event.to !== 'attachment') {
                zoneArray(state, inst.ownerId, event.to, where).push(inst.id);
            }
            inst.zone = event.to;
            return state;
        }
        case 'DamageDealt': {
            const where = 'applyEvent(DamageDealt)';
            if (event.target.kind === 'unit') {
                const inst = requireInstance(state, event.target.instanceId, where);
                // Damage is paid from current EFFECTIVE health. A temporary maxHealth
                // cap must not hide hits in raw stats (for example 5 raw / 3 effective
                // receiving 1 damage is now 2 health, not still visually 3/3).
                if (event.amount !== 0) {
                    const before = effectiveStats(state, event.target.instanceId).health;
                    inst.stats.health = normalizeZero(before - event.amount);
                    if (event.sourceId !== undefined)
                        inst.vars.lastDamageSourceId = event.sourceId;
                }
            }
            else {
                const player = requirePlayer(state, event.target.playerId, where);
                player.health = normalizeZero(player.health - event.amount);
                const source = event.sourceId === undefined ? undefined : state.instances[event.sourceId];
                if (event.amount > 0 && source !== undefined && source.controllerId !== player.id) {
                    const previous = Array.isArray(source.vars.damagedPlayerIds)
                        ? source.vars.damagedPlayerIds.filter((id) => typeof id === 'string')
                        : [];
                    if (!previous.includes(player.id))
                        source.vars.damagedPlayerIds = [...previous, player.id];
                }
            }
            return state;
        }
        case 'PlayerHealthRestored': {
            const where = 'applyEvent(PlayerHealthRestored)';
            const player = requirePlayer(state, event.playerId, where);
            if (!Number.isSafeInteger(event.previous) || !Number.isSafeInteger(event.current)
                || !Number.isSafeInteger(event.amount) || event.previous !== player.health
                || event.amount <= 0 || event.current - event.previous !== event.amount) {
                throw new Error(`${where}: неконсистентные previous/current/amount`);
            }
            player.health = event.current;
            return state;
        }
        case 'UnitHealed': {
            const where = 'applyEvent(UnitHealed)';
            const inst = requireInstance(state, event.instanceId, where);
            if (!Number.isSafeInteger(event.previous) || !Number.isSafeInteger(event.current)
                || !Number.isSafeInteger(event.amount) || event.previous !== inst.stats.health
                || event.amount <= 0 || event.current - event.previous !== event.amount) {
                throw new Error(`${where}: неконсистентные previous/current/amount`);
            }
            inst.stats.health = event.current;
            return state;
        }
        case 'StatChanged': {
            const where = 'applyEvent(StatChanged)';
            const inst = requireInstance(state, event.instanceId, where);
            const stat = event.stat;
            if (stat !== 'attack' && stat !== 'health' && stat !== 'maxHealth') {
                throw new Error(`${where}: неизвестный стат "${stat}" (ожидался attack | health | maxHealth)`);
            }
            // Дельта в событии — уже ФАКТИЧЕСКИ применённая (кап посчитан операцией
            // modifyStat, tech/21 §7.1) — reducer применяет её буквально.
            inst.stats[stat] = normalizeZero(inst.stats[stat] + event.delta);
            return state;
        }
        case 'EffectAdded': {
            const where = 'applyEvent(EffectAdded)';
            if (event.effect.id !== event.effectId) {
                throw new Error(`${where}: effectId "${event.effectId}" не совпадает с effect.id "${event.effect.id}"`);
            }
            if (state.effects.some((e) => e.id === event.effectId)) {
                throw new Error(`${where}: эффект "${event.effectId}" уже существует`);
            }
            // Клонируем: состояние не должно разделять ссылки с объектом события.
            state.effects.push(structuredClone(event.effect));
            if (event.effect.seq + 1 > state.nextEffectSeq) {
                state.nextEffectSeq = event.effect.seq + 1;
            }
            return state;
        }
        case 'EffectCounterChanged': {
            const where = 'applyEvent(EffectCounterChanged)';
            const effect = state.effects.find((candidate) => candidate.id === event.effectId);
            if (effect === undefined)
                throw new Error(`${where}: неизвестный эффект "${event.effectId}"`);
            if (effect.defId !== event.defId || JSON.stringify(effect.scope) !== JSON.stringify(event.scope)) {
                throw new Error(`${where}: публичная идентичность эффекта не совпадает с состоянием`);
            }
            const stored = effect.vars[event.key];
            const previous = stored === undefined ? 0 : stored;
            if (!Number.isSafeInteger(previous) || previous < 0) {
                throw new Error(`${where}: vars.${event.key} не является неотрицательным safe integer`);
            }
            if (!Number.isSafeInteger(event.previous) || !Number.isSafeInteger(event.current)
                || !Number.isSafeInteger(event.delta) || event.previous < 0 || event.current < 0
                || event.previous !== previous || event.current - event.previous !== event.delta) {
                throw new Error(`${where}: неконсистентные previous/current/delta`);
            }
            effect.vars[event.key] = event.current;
            return state;
        }
        case 'EffectRemoved': {
            const where = 'applyEvent(EffectRemoved)';
            const i = state.effects.findIndex((e) => e.id === event.effectId);
            if (i < 0) {
                throw new Error(`${where}: неизвестный эффект "${event.effectId}"`);
            }
            state.effects.splice(i, 1);
            return state;
        }
        case 'UnitDied': {
            const where = 'applyEvent(UnitDied)';
            const inst = requireInstance(state, event.instanceId, where);
            clearLastDamageSourceReferences(state, inst.id);
            const cell = requireCell(state, event.cellId, where);
            if (cell.occupantId === inst.id) {
                cell.occupantId = null;
            }
            // Освобождаем ячейку носителя, но экземпляр НЕ удаляем здесь: его onDeath
            // диспетчеризуется на ОТЛОЖЕННОМ дренаже (после этого события), и хуку нужен
            // живой ctx.self. Судьба сущности — ПОСЛЕ триггеров:
            //  - токен (D25): удаляется из instances пост-дренажным sweep'ом (finalizeDeadUnits);
            //  - обычная карта: зонный перенос board→discard делает ПАРНЫЙ CardMoved (destroy).
            // Помечаем экземпляр «сошедшим с поля» очисткой cellId (zone board + нет cellId),
            // чтобы sweep и предикат «жив» его различали, а онлайн-запрос по ячейке не находил.
            if (inst.cellId === event.cellId) {
                delete inst.cellId;
            }
            // Persisted legacy-name counter counts every token Гибель, including timed.
            if (event.isToken) {
                requirePlayer(state, event.controllerIdAtDeath ?? event.ownerId, where).ordinaryTokenDeaths += 1;
            }
            return state;
        }
        case 'TokenRemoved': {
            const where = 'applyEvent(TokenRemoved)';
            const inst = requireInstance(state, event.instanceId, where);
            if (inst.isToken !== true || inst.cellId !== undefined) {
                throw new Error(`${where}: экземпляр "${inst.id}" не является погибшим токеном`);
            }
            delete state.instances[inst.id];
            return state;
        }
        case 'TokenCreated': {
            const where = 'applyEvent(TokenCreated)';
            if (state.instances[event.instanceId] !== undefined) {
                throw new Error(`${where}: экземпляр "${event.instanceId}" уже существует`);
            }
            const owner = requirePlayer(state, event.ownerId, where);
            if (event.cellId !== undefined) {
                const cell = requireCell(state, event.cellId, where);
                if (cell.occupantId !== null) {
                    throw new Error(`${where}: ячейка "${event.cellId}" занята ("${cell.occupantId}")`);
                }
                cell.occupantId = event.instanceId;
            }
            state.instances[event.instanceId] = makeCardInstance({
                id: event.instanceId,
                seq: event.seq,
                defId: event.defId,
                ownerId: event.ownerId,
                // Токен без ячейки создаётся В РУКЕ владельца («создать карту в руке»);
                // токен с ячейкой — сразу на поле (createToken, tech/21 §7).
                zone: event.cellId !== undefined ? 'board' : 'hand',
                ...(event.cellId !== undefined ? { cellId: event.cellId } : {}),
                isToken: true,
                stats: event.stats,
                vars: event.vars,
            });
            if (event.cellId === undefined) {
                owner.hand.push(event.instanceId);
            }
            if (event.seq + 1 > state.nextInstanceSeq) {
                state.nextInstanceSeq = event.seq + 1;
            }
            return state;
        }
        case 'TokenCreationSkipped':
        case 'AssemblyCompleted':
        case 'CardsAbsorbed':
        case 'RandomDamageReplacementResolved':
        case 'RandomUnitSelectionResolved':
            return state;
        case 'TimedDestroyScheduled': {
            if (state.timedDestroys.some((marker) => marker.id === event.marker.id)) {
                throw new Error(`applyEvent(TimedDestroyScheduled): marker "${event.marker.id}" уже существует`);
            }
            state.timedDestroys.push(structuredClone(event.marker));
            state.nextTimedDestroySeq = Math.max(state.nextTimedDestroySeq, event.marker.seq + 1);
            return state;
        }
        case 'TimedDestroyResolved': {
            const index = state.timedDestroys.findIndex((marker) => marker.id === event.markerId);
            if (index < 0) {
                throw new Error(`applyEvent(TimedDestroyResolved): marker "${event.markerId}" не существует`);
            }
            state.timedDestroys.splice(index, 1);
            return state;
        }
        case 'CardConsumed': {
            const where = 'applyEvent(CardConsumed)';
            const inst = requireInstance(state, event.instanceId, where);
            const cell = requireCell(state, event.cellId, where);
            if (inst.zone !== 'board' || inst.cellId !== event.cellId || cell.occupantId !== inst.id) {
                throw new Error(`${where}: карта "${inst.id}" не занимает ячейку "${event.cellId}"`);
            }
            clearLastDamageSourceReferences(state, inst.id);
            cell.occupantId = null;
            if (inst.isToken === true) {
                delete state.instances[inst.id];
            }
            else {
                delete inst.cellId;
                inst.zone = 'discard';
                requirePlayer(state, inst.ownerId, where).discard.push(inst.id);
            }
            return state;
        }
        case 'CardTransformed': {
            const inst = requireInstance(state, event.instanceId, 'applyEvent(CardTransformed)');
            inst.defId = event.toDefId;
            inst.stats = structuredClone(event.stats);
            inst.enteredRound = event.enteredRound;
            inst.vars = {};
            delete inst.heat;
            delete inst.freeze;
            delete inst.hack;
            return state;
        }
        case 'TemporaryEnergyGranted': {
            const player = requirePlayer(state, event.playerId, 'applyEvent(TemporaryEnergyGranted)');
            const currentRound = player.resources.temporaryEnergyRound;
            if (currentRound === null || currentRound === event.availableRound) {
                player.resources.temporaryEnergy += event.amount;
                player.resources.temporaryEnergyRound = event.availableRound;
                return state;
            }
            const queuedRound = player.resources.queuedTemporaryEnergyRound;
            if (queuedRound !== undefined && queuedRound !== event.availableRound) {
                throw new Error('applyEvent(TemporaryEnergyGranted): поддерживаются только текущий и следующий бонусы');
            }
            player.resources.queuedTemporaryEnergy =
                (player.resources.queuedTemporaryEnergy ?? 0) + event.amount;
            player.resources.queuedTemporaryEnergyRound = event.availableRound;
            return state;
        }
        case 'TemporaryEnergyExpired': {
            const player = requirePlayer(state, event.playerId, 'applyEvent(TemporaryEnergyExpired)');
            player.resources.temporaryEnergy = 0;
            player.resources.temporaryEnergyRound = null;
            return state;
        }
        case 'CardCreated': {
            const where = 'applyEvent(CardCreated)';
            if (state.instances[event.instanceId] !== undefined)
                throw new Error(`${where}: экземпляр уже существует`);
            const owner = requirePlayer(state, event.ownerId, where);
            state.instances[event.instanceId] = makeCardInstance({
                id: event.instanceId,
                seq: event.seq,
                defId: event.defId,
                ownerId: event.ownerId,
                zone: 'hand',
                stats: event.stats,
            });
            owner.hand.push(event.instanceId);
            state.nextInstanceSeq = Math.max(state.nextInstanceSeq, event.seq + 1);
            return state;
        }
        case 'RuleChanged': {
            const v = event.value;
            state.rules[event.key] = typeof v === 'number' ? normalizeZero(v) : v;
            return state;
        }
        case 'VarChanged': {
            const where = 'applyEvent(VarChanged)';
            // Клонируем значение: состояние не должно разделять ссылку с объектом
            // события (журнал событий — исторический факт, живёт отдельно, D27/R14).
            const value = typeof event.value === 'number' ? normalizeZero(event.value) : structuredClone(event.value);
            const target = event.target;
            if (target.kind === 'unit') {
                const inst = requireInstance(state, target.instanceId, where);
                inst.vars[event.key] = value;
            }
            else if (target.kind === 'effect') {
                const eff = state.effects.find((e) => e.id === target.effectId);
                if (eff === undefined) {
                    throw new Error(`${where}: неизвестный эффект "${target.effectId}"`);
                }
                eff.vars[event.key] = value;
            }
            else {
                const attachment = state.attachments.find((candidate) => candidate.attachmentId === target.attachmentId);
                if (attachment === undefined)
                    throw new Error(`${where}: неизвестный attachment "${target.attachmentId}"`);
                attachment.vars[event.key] = value;
            }
            return state;
        }
        case 'MatchEnded': {
            state.status = { phase: 'ended', winner: event.winner };
            return state;
        }
        default:
            return assertNever(event);
    }
}
//# sourceMappingURL=reducer.js.map